// frontend/src/components/librarian/dashboard/StatTile.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

interface Props {
  label: string;
  /** Undefined while loading: an em dash holds the space. */
  value?: number | string;
  icon: React.ComponentType<{ className?: string }>;
  /** A real comparison the API returned, e.g. `{ value: 3.2, label: 'vs previous 30 days' }`. */
  delta?: { value: number; label: string };
  /** One quiet line under the value, e.g. "12 added today". */
  caption?: string;
  tone?: 'default' | 'danger';
  /** Where the tile leads; without it the tile is static. */
  to?: string;
}

/** A dashboard counter: icon disc, sentence-case label, the number, its movement. */
const StatTile: React.FC<Props> = ({ label, value, icon: Icon, delta, caption, tone = 'default', to }) => {
  const danger = tone === 'danger' && typeof value === 'number' && value > 0;
  const body = (
    <>
      <span
        aria-hidden
        className={`h-11 w-11 shrink-0 rounded-full inline-flex items-center justify-center ${
          danger ? 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)]' : 'bg-[var(--lib-gold)]/15 text-[var(--lib-gold-text)]'
        }`}
      >
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] text-[var(--lib-ink-muted)]">{label}</span>
        <span
          className={`block mt-0.5 font-display text-[26px] leading-[30px] font-semibold tabular-nums ${
            danger ? 'text-[var(--lib-danger)]' : 'text-[var(--lib-ink)]'
          }`}
        >
          {value === undefined ? '—' : typeof value === 'number' ? value.toLocaleString() : value}
        </span>
        {delta && (
          <span className="mt-1.5 flex items-center gap-1 text-[11px] tabular-nums">
            {delta.value >= 0 ? (
              <ArrowUpRight aria-hidden className="h-3 w-3 text-[var(--lib-success)]" />
            ) : (
              <ArrowDownRight aria-hidden className="h-3 w-3 text-[var(--lib-danger)]" />
            )}
            <span className={delta.value >= 0 ? 'text-[var(--lib-success)]' : 'text-[var(--lib-danger)]'}>
              {Math.abs(delta.value)}%
            </span>
            <span className="text-[var(--lib-ink-muted)]">{delta.label}</span>
          </span>
        )}
        {caption && (
          <span className={`block text-[11px] text-[var(--lib-ink-muted)] tabular-nums ${delta ? 'mt-0.5' : 'mt-1.5'}`}>
            {caption}
          </span>
        )}
      </span>
    </>
  );
  const cls =
    'flex items-start gap-4 w-full rounded-lg border border-[var(--lib-hairline)] bg-[var(--lib-surface)] p-5 text-left';
  return to ? (
    <Link
      to={to}
      aria-label={`${label}: ${value === undefined ? 'loading' : value}`}
      className={`${cls} transition-colors hover:border-[var(--lib-gold)]`}
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
};

export default StatTile;
